import { useCallback, useMemo, useState } from 'react';
import { langs } from '@pypestream/design-system';
import { throwErrorWithId } from '@pypestream/utils';

import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';
import { useEditorModal } from '../components/modals/editor-modal';
import { tableStore } from '../stores/table-store';
import {
  uploadFile,
  blobToFile,
  createFileBlob,
  DataFileType,
  progressServerConfig,
} from '../utils';
import { useProStudioViewerRole } from './use-pro-studio-viewer-role';
import { useBobcatFlag } from './use-bobcat-flag';
import { resolveEditorContent } from '../pages/action-editor-content';
import { useSecretHiddenNotice } from '../pages/use-secret-hidden-notice';

export type ScriptFileRef = {
  /** File name as shown, e.g. `AccountLookup.py`. */
  name: string;
  /** Path in the draft, e.g. `services/AccountLookup.py`. */
  path: string;
  type: Omit<DataFileType, 'assetData'>;
};

/**
 * Opens a draft script (or config file) in the code editor and saves it back
 * to the draft — the Actions page's editor, shared so the node panel can open
 * the script behind an action node's Command. Viewers get it read-only, and
 * a file holding a private key is shown without it (`action-editor-content.ts`).
 */
export const useScriptEditor = () => {
  const isViewer = useProStudioViewerRole();
  const { projectId, versionId } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    versionId:
      ctx.customerId && ctx.botName && ctx.draftVersion
        ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
        : undefined,
  }));

  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<
    | {
        fileName: string;
        fileData: string;
        type: Omit<DataFileType, 'assetData'>;
      }
    | undefined
  >();

  const pythonLangs = useMemo(() => [langs.python()], []);

  const handleSaveScript = (editorCode: string) => {
    const { fileName, type } = fileData || {};

    if (!projectId) {
      throwErrorWithId('Project ID is required');
    }

    if (!fileName) {
      throwErrorWithId('File name is required');
      return;
    }

    if (!versionId) {
      throwErrorWithId('Version ID is required');
    }

    if (!type) {
      throwErrorWithId('Type is required');
    }

    setIsLoading(true);

    uploadFile({
      url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${type === 'scriptFile' ? 'scripts' : 'config'}`,
      file: blobToFile(createFileBlob(editorCode, fileName), fileName),
      type: type as DataFileType,
      onChange: ({ data }) => {
        sendProStudioEvent({
          type: 'updateDraftData',
          draft: data as ProStudioContext['draft'],
          action: 'update',
        });

        tableStore.markDraftFilesChanged();
        setIsLoading(false);
      },
      progress: (c, l, total) =>
        progressServerConfig(c, l, total, (p) => setProgress(p)),
    });
  };

  // A private key is never handed to the editor; see resolveEditorContent.
  // Memoised on its three inputs so the credential scan of the file text runs
  // when one of them changes, not on every render.
  // Only with Bobcat's master flag on; off, the editor is candidate's (DoD #2).
  const secretHiddenNotice = useSecretHiddenNotice();
  const withholdKeys = useBobcatFlag('fiveStageNav') === 'allowed';
  const fileText = fileData?.fileData;
  const editorContent = useMemo(
    () =>
      resolveEditorContent(fileText, secretHiddenNotice, {
        isViewer,
        withholdKeys,
      }),
    [fileText, secretHiddenNotice, isViewer, withholdKeys]
  );

  const { editorModal, openModal } = useEditorModal({
    loading: isLoading,
    progress,
    code: editorContent.code,
    title: fileData?.fileName,
    langs: pythonLangs,
    notice: editorContent.notice,
    viewOnly: editorContent.viewOnly,
    onSave: handleSaveScript,
    onClose: () => {
      setFileData(undefined);
      setProgress(undefined);
      setIsLoading(false);
    },
  });

  const openScript = useCallback(
    ({ name, path, type }: ScriptFileRef) => {
      sendProStudioEvent({
        type: 'editScript',
        path,
        name,
        cb: (data) => {
          setFileData({ fileData: data, fileName: name, type });
          openModal();
        },
      });
    },
    [openModal]
  );

  return { editorModal, openScript };
};
